import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { TrackingProvider } from '@act/react-components/tracking';
import {
  CallCreationRequestForm,
  CallCreationRequestSchema,
  CallDto,
  ConsentGoalDto,
  NeedDto,
  ScheduledEventDto,
  SurveyV2,
  UpdateCallForm,
  UpdateCallSchema,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  callsAppSelectors,
  goalsTrackingSelectors,
  selectFeedItemByFeedItemId,
  selectMemberById,
  selectSelectedMember,
} from 'redux/redux';
import { callsRequests } from 'redux/requests/calls.request';
import { useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { Goal } from 'stories/containers/GoalsTracking/types';
import { CallFormContent } from './CallFormContent';
import { CallFullScreen } from './CallFullScreen';
import { CallGoalsPanel } from './CallGoalsPanel';
import { CallHeader } from './CallHeader';
import { CallUpdateForm } from './CallUpdateForm';
import { createCallFormConfig } from './CreateCallForm.config';
import {
  getSidePanelIsDirty,
  setCallFullScreenSaveAndClose,
} from './FullScreenSaveClose.utils';
import { PostCallPanel } from './PostCall/PostCallPanel';

interface CallsFullScreenContentProps {
  handleClose: () => void;
  open: boolean;
  call?: CallDto;
  user?: UserDto;
  need?: NeedDto;
  scheduledEvent?: ScheduledEventDto;
  isFeedbackCall?: boolean;
  mode: 'Create' | 'Edit';
  isCustomResourcing?: boolean;
}

export const CallsFullScreenContent = ({
  handleClose,
  open,
  call,
  user,
  need,
  scheduledEvent,
  isFeedbackCall,
  mode,
}: CallsFullScreenContentProps) => {
  const [selectedNeedId, setSelectedNeedId] = useState<number | undefined>(
    undefined
  );
  const [selectedSurvey, setSelectedSurvey] = useState<
    SurveyV2.AssignedSurveyDto | undefined
  >(undefined);
  const [selectedConsent, setSelectedConsent] = useState<
    ConsentGoalDto | undefined
  >(undefined);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<
    string | undefined
  >(undefined);

  const [newCall, isCreateCallLoading, createCall] = useApiCall(
    callsRequests.createCall
  );
  const currentCall = callsAppSelectors.useSelectCallById(
    call?.id ?? newCall?.id
  );
  const suggestions = callsAppSelectors.useSelectSuggestionsByCallId(call?.id);
  const [isPostCall, setIsPostCall] = useState(
    !!currentCall?.recording?.llmGeneratedNotes ||
      !!currentCall?.recording?.recordingTranscription ||
      !!currentCall?.recording?.summary
  );
  const [isResourceCall, setIsResourceCall] = useState(false);
  const [openAddNeed, setOpenAddNeed] = useState(false);
  const saveFunctionRef = useRef<(() => Promise<void>) | null>(null);

  const handleGoalClick = (goal: Goal) => {
    if (goal.goalType === 'need') {
      setSelectedNeedId(goal.id);
    }
    if (goal.goalType === 'survey') {
      setSelectedSurvey(goal as SurveyV2.AssignedSurveyDto);
    }
    if (goal.goalType === 'consent') {
      setSelectedConsent(goal as ConsentGoalDto);
    }
  };

  const isEditMode = mode === 'Edit' || !!newCall || !!currentCall;

  const primaryPhoneNumber = useMemo(() => {
    return user?.phoneNumbers.find(phn => phn.primary);
  }, [user?.phoneNumbers]);
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState,
    reset,
  } = useForm<UpdateCallForm | CallCreationRequestForm>({
    mode: 'onChange',
    defaultValues: isEditMode
      ? {
          note: call?.note ?? currentCall?.note ?? '<p><br></p>',
          outcomeId: call?.outcomeId ?? currentCall?.outcomeId ?? null,
          resourceTitle: call?.resourceInteraction?.resource.title ?? '',
          resourceInteraction: call?.resourceInteraction ?? null,
          needResourceInteraction: call?.needResourceInteraction ?? null,
          callAuditorId: call?.auditorId ?? '',
          userNeedId: call?.needResourceInteraction?.userNeedId ?? null,
        }
      : {
          phoneNumberId: primaryPhoneNumber?.id ?? null,
          phoneNumber: null,
          scheduledEventId: null,
          userNeedId: null,
          resourceInteraction: {
            title: '',
            phoneNumber: '',
          },
          needResourceInteraction: null,
          userNeedRequest: null,
          callAuditorId: '',
          resourceTitle: '',
        },
    resolver: zodResolver(
      isEditMode ? UpdateCallSchema : CallCreationRequestSchema
    ),
  });

  const callFeedItemSelector = useMemo(
    () => selectFeedItemByFeedItemId(call?.id, 'call'),
    [call]
  );

  const handleUnsavedChanges = useUnsavedChangesDialog();
  const callFeedItem = useAppSelector(callFeedItemSelector);
  const selectedCall = callsAppSelectors.useSelectCallById(
    callFeedItem?.externalItemId
  );

  const memberById = useAppSelector(selectMemberById(selectedCall?.subjectId));
  const selectedMemberFromStore = useAppSelector(selectSelectedMember);

  const selectedMember = selectedCall?.subjectId
    ? memberById
    : user || selectedMemberFromStore;

  const aggregatedGoals =
    goalsTrackingSelectors.useSelectAggregatedGoalsForMember(
      selectedMember?.id
    );

  const goalsLoadingStatus =
    goalsTrackingSelectors.useSelectGoalsTrackingStatus();

  const goals = useMemo(() => {
    const inProgressGoals = aggregatedGoals?.inProgress || [];
    const hospitalizationSurvey = inProgressGoals.find(
      goal => goal?.survey?.key === 'hospitalization-follow-up'
    );
    const allOtherGoals = inProgressGoals.filter(
      goal => goal?.survey?.key !== 'hospitalization-follow-up'
    );
    return hospitalizationSurvey
      ? [hospitalizationSurvey, ...allOtherGoals]
      : allOtherGoals;
  }, [aggregatedGoals]);

  const latestCall = selectedCall || currentCall;
  const phoneNumberOptions = useMemo(() => {
    if (!user?.phoneNumbers) return [];

    return user.phoneNumbers.map(phn => ({
      label: `${phn.description ?? `Phone Number ${phn.id}`} - ${phn.number}`,
      value: phn.id,
      number: phn.number,
    }));
  }, [user?.phoneNumbers]);

  const watchPhoneNumberId = watch('phoneNumberId');
  const selectedPhoneNumber = phoneNumberOptions.find(
    option => option.value === watchPhoneNumberId
  );

  const findMemberNumber =
    latestCall?.receiver?.userId === selectedMember?.id
      ? latestCall?.receiver?.phoneNumber
      : latestCall?.originator?.phoneNumber;

  const memberNumber = useMemo(() => {
    if (selectedPhoneNumber?.label) return selectedPhoneNumber.label;

    if (findMemberNumber) {
      const phoneNumberDescription = user?.phoneNumbers.find(
        phoneNumber => phoneNumber.number === findMemberNumber
      )?.description;
      return phoneNumberDescription
        ? `${phoneNumberDescription} - ${findMemberNumber}`
        : findMemberNumber;
    }
    if (selectedPhoneNumberId) {
      const phoneNumberDescription = user?.phoneNumbers.find(
        phoneNumber => phoneNumber.number === selectedPhoneNumberId.toString()
      )?.description;
      return phoneNumberDescription
        ? `${phoneNumberDescription} - ${selectedPhoneNumberId}`
        : selectedPhoneNumberId;
    }

    return '';
  }, [selectedPhoneNumber, findMemberNumber, user?.phoneNumbers]);

  const startCall = async (callDetails: CallCreationRequestForm) => {
    setSelectedPhoneNumberId(callDetails?.phoneNumber);
    createCall({
      params: null,
      body: {
        targetUserId: user?.id,
        phoneNumber: callDetails.phoneNumber,
        isFeedbackCall: isFeedbackCall,
        ...(need && { userNeedId: need.id }),
        ...(callDetails?.phoneNumberId && {
          phoneNumberId: callDetails?.phoneNumberId,
        }),
        ...(scheduledEvent?.id && { scheduledEventId: scheduledEvent?.id }),
        ...(need ||
          (callDetails?.userNeedId && {
            needId: need?.id || callDetails?.userNeedId,
          })),
        ...(callDetails?.resourceInteraction &&
          (!!need || !!callDetails.userNeedId) && {
            needResourceInteraction: {
              title: callDetails?.resourceInteraction?.title,
              phoneNumber: callDetails?.phoneNumber,
              userNeedId: callDetails?.userNeedId,
            },
          }),
      },
    });
  };

  const { isDirty } = formState;

  const handleCloseWithUnsavedCheck = () => {
    if (isDirty || !!getSidePanelIsDirty()) {
      void handleUnsavedChanges(handleClose);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (isResourceCall) {
      setValue('phoneNumberId', undefined, { shouldValidate: true });
    }
  }, [isResourceCall]);

  const handleCancel = handleCloseWithUnsavedCheck;
  const handleSaveClick = () => {
    if (!currentCall?.id || !isDirty) {
      handleClose();
      return;
    }
    const callForm = document.getElementById('call-form') as HTMLFormElement;
    if (callForm) {
      callForm.requestSubmit();
    }
  };

  const handleSaveAndClose = useCallback(async () => {
    if (
      isEditMode &&
      currentCall?.id &&
      (isDirty || !!getSidePanelIsDirty()) &&
      saveFunctionRef.current
    ) {
      try {
        handleCloseWithUnsavedCheck();
      } catch (error) {
        console.error('Error saving call:', error);
      }
    } else {
      handleClose();
    }
  }, [isEditMode, currentCall?.id, isDirty, handleClose]);

  // Expose save and close function globally when dialog is open
  useEffect(() => {
    if (open) {
      setCallFullScreenSaveAndClose(handleSaveAndClose);
    } else {
      setCallFullScreenSaveAndClose(null);
    }
    return () => {
      setCallFullScreenSaveAndClose(null);
    };
  }, [open, handleSaveAndClose]);

  const formConfigComp = useMemo(
    () => createCallFormConfig(isResourceCall, goals, phoneNumberOptions),
    [isResourceCall, goals, phoneNumberOptions]
  );

  const resourceNeed = watch('userNeedId');
  useEffect(() => {
    setSelectedNeedId(resourceNeed);
  }, [resourceNeed]);

  useEffect(() => {
    if (!!currentCall || isEditMode) {
      reset({
        note: currentCall?.note ?? '<p><br></p>',
        outcomeId: currentCall?.outcomeId ?? null,
        resourceTitle:
          currentCall?.resourceInteraction?.resource?.title ?? undefined,
        resourceInteraction: currentCall?.resourceInteraction ?? undefined,
        needResourceInteraction:
          currentCall?.needResourceInteraction ?? undefined,
        callAuditorId: currentCall?.auditorId ?? undefined,
      });
    }
  }, [currentCall?.id, isEditMode, reset]);

  const callRecording = currentCall?.recording;

  useEffect(() => {
    if (
      !!callRecording?.llmGeneratedNotes ||
      !!callRecording?.recordingTranscription ||
      !!callRecording?.summary ||
      !!suggestions?.needs.length ||
      !!suggestions?.actions.length ||
      !!suggestions?.scheduledEvents.length
    ) {
      setIsPostCall(true);
    }
  }, [callRecording, suggestions]);

  const userNeeds = useMemo(() => {
    return goals
      .filter(goal => goal.goalType === 'need')
      .map(goal => ({
        label: 'issueLabel' in goal ? goal.issueLabel : 'Need',
        value: goal.id,
      }));
  }, [goals]);

  const editContent = (
    <CallUpdateForm
      handleClose={handleClose}
      open={open}
      call={currentCall}
      user={user}
      need={need}
      scheduledEvent={scheduledEvent}
      control={control}
      getValues={getValues}
      setValue={setValue}
      watch={watch}
      errors={formState.errors}
      formState={formState}
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      isCustomResourcing={isResourceCall}
      userNeeds={userNeeds}
      onSaveFunctionReady={saveFn => {
        saveFunctionRef.current = saveFn;
      }}
    />
  );

  const leftContent = (
    <CallFormContent
      control={control}
      memberName={selectedMember?.firstName}
      formState={formState}
      formConfig={formConfigComp}
      isResourceCall={isResourceCall}
      reset={reset}
      onToggleResourceCall={() => setIsResourceCall(!isResourceCall)}
    />
  );

  // Shared props for both CallGoalsPanel and PostCallPanel
  const goalsPanelProps = useMemo(
    () => ({
      selectedMember,
      goals,
      goalsLoadingStatus,
      selectedNeedId,
      onGoalClick: handleGoalClick,
      setSelectedNeedId,
      onSaveClick: handleSaveClick,
      setSelectedSurvey,
      selectedSurvey,
      selectedConsent,
      setSelectedConsent,
      setOpenAddNeed,
      openAddNeed,
      call: currentCall,
    }),
    [
      selectedMember,
      goals,
      goalsLoadingStatus,
      selectedNeedId,
      handleGoalClick,
      handleSaveClick,
      setSelectedSurvey,
      selectedSurvey,
      selectedConsent,
      setSelectedConsent,
      setOpenAddNeed,
      openAddNeed,
      currentCall,
      setSelectedNeedId,
    ]
  );

  const rightContent = isPostCall ? (
    <TrackingProvider contextName="post-call-panel">
      <PostCallPanel {...goalsPanelProps} />
    </TrackingProvider>
  ) : (
    <TrackingProvider contextName="call-goals-panel">
      <CallGoalsPanel {...goalsPanelProps} />
    </TrackingProvider>
  );

  const isResource =
    !!currentCall?.needResourceInteraction?.resource?.title || isResourceCall;

  const topContentLeftOnly =
    !!selectedNeedId ||
    !!selectedSurvey ||
    !!selectedConsent ||
    !!openAddNeed ||
    isPostCall;

  const resourceTitle =
    currentCall?.needResourceInteraction?.resource?.title ??
    watch('resourceInteraction.title');

  const resourceNumber = watch('resourceInteraction.phoneNumber');

  useEffect(() => {
    if (newCall) {
      reset();
    }
  }, [newCall?.id, reset]);

  const handleBeginCall = async () => {
    if (isResourceCall) {
      await startCall({
        resourceInteraction: {
          title: getValues('resourceInteraction.title'),
          phoneNumber: getValues('resourceInteraction.phoneNumber'),
        },
        phoneNumber: getValues('resourceInteraction.phoneNumber'),
        userNeedId: getValues('userNeedId'),
      });
    } else {
      const selectedPhoneNumber = phoneNumberOptions.find(
        option => option.value === getValues('phoneNumberId')
      );
      if (selectedPhoneNumber) {
        await startCall({
          phoneNumber: selectedPhoneNumber.number,
          phoneNumberId: selectedPhoneNumber.value,
        });
      }
    }
  };
  const title = getValues('resourceInteraction.title');
  const phoneNumber = getValues('resourceInteraction.phoneNumber');
  const userNeedId = getValues('userNeedId');
  const phoneNumberId = watch('phoneNumberId');
  const isDisabledResourceCall = !title || !phoneNumber || !userNeedId;
  const disabledBeginCall = !phoneNumberId && isDisabledResourceCall;

  const topContent = (
    <CallHeader
      currentCall={currentCall}
      newCall={newCall}
      selectedMember={selectedMember}
      memberNumber={memberNumber}
      isResource={isResource}
      resourceTitle={resourceTitle}
      resourceNumber={resourceNumber}
      topContentLeftOnly={topContentLeftOnly}
      onBeginCall={handleBeginCall}
      disabledBeginCall={disabledBeginCall}
      isCreateCallLoading={isCreateCallLoading}
    />
  );

  return (
    <TrackingProvider contextName="call-fullscreen">
      <CallFullScreen
        open={open}
        handleClose={handleCloseWithUnsavedCheck}
        leftContent={isEditMode ? editContent : leftContent}
        rightContent={rightContent}
        topContent={topContent}
        drawerIsOpen={topContentLeftOnly}
      />
    </TrackingProvider>
  );
};
